// frontend/src/components/operator/UnmatchedSignalsPanel.tsx
import { apiUrl, API_BASE_URL } from '../../config/paths';
// Панель несопоставленных сигналов
// Работает с существующим signal-matcher.ts (suggestions)
//
// ВЕРСИЯ: 3.3.3 HYBRID (2026-01-30)
// ИЗМЕНЕНИЯ:
// - v3.3.3: HYBRID версия - полный функционал v3.3.1 + улучшения GPT v3.3.2
// - v3.3.3: AbortController для fetch в loadSignals (cleanup pending requests)
// - v3.3.3: Interval увеличен до 60000ms (1min) для production
// - v3.3.1: GROK FIX - useRef для onSignalsLoaded (предотвращение infinite loop)
// - v3.3.1: loadSignals больше НЕ зависит от onSignalsLoaded в useCallback
// - v3.3.0: Добавлен onSignalsLoaded() для предотвращения дубля данных с SignalsUnmatchedTable
// - v3.3.0: Добавлен слушатель события "refresh-unmatched-signals" (таблица может инициировать refresh панели)
// - v3.2.0: ИСПРАВЛЕНЫ синтаксические ошибки кавычек в apiUrl()
// - v3.2.0: ДОБАВЛЕНА авторизация (Authorization header) во все fetch запросы
// - Добавлены прямые кнопки ITU MARS и Inmarsat в карточку сигнала
// - Кнопки всегда видны без необходимости делать API запрос
// - PROTECTED: Кнопки ITU MARS и Inmarsat - НЕ УДАЛЯТЬ!
// - v3.1.0: SSAS_ALERT исключены из списка (нельзя связывать с заявками)
// - v3.3.4: Tailwind className → inline styles (Tailwind не подключён)

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Typography, Button } from '@mui/material';
import { getShiftPeriod } from '../../utils/shiftPeriod';

// Импорт функции проверки реальных тревог
import { isRealAlert } from '../../utils/statusLabels';

// ═══════════════════════════════════════════════════════════════
// ТИПЫ (совместимые с signal-matcher.ts)
// ═══════════════════════════════════════════════════════════════

interface Suggestion {
  requestId: number;
  score: number;
  reasons: string[];
}

interface SignalEnrichment {
  source: 'CACHE' | 'ITU_MARS' | 'INMARSAT' | 'NOT_FOUND';
  confidence: number;
  vessel_name?: string;
  imo_number?: string;
  mmsi?: string;
  call_sign?: string;
  flag?: string;
  owner?: string;
  itu_mars_message?: string;
  manual_check_urls?: { name: string; url: string; description: string }[];
  auto_open_url?: string;
  requiresManualCheck?: boolean;
  origin_source?: string;
}

export interface UnmatchedSignal {
  id: number;
  terminal_number: string;
  mmsi?: string;
  vessel_name?: string;
  received_at: string;
  signal_type: string;
  status: string;
  suggestions?: Suggestion[];
  operator_messages?: string[];
  enrichment?: SignalEnrichment;
}

interface Props {
  onSignalLinked?: (signalId: number, requestId: number) => void;
  onSignalDismissed?: (signalId: number, reason: string) => void;

  // v3.3.0: отдаём наверх фид, чтобы SignalsUnmatchedTable мог отображать те же данные без повторного fetch
  onSignalsLoaded?: (signals: UnmatchedSignal[]) => void;
}

// ═══════════════════════════════════════════════════════════════
// ВНЕШНИЕ РЕСУРСЫ ДЛЯ РУЧНОЙ ПРОВЕРКИ
// PROTECTED: НЕ УДАЛЯТЬ ЭТУ СЕКЦИЮ!
// ═══════════════════════════════════════════════════════════════

const EXTERNAL_RESOURCES = {
  ITU_MARS: {
    name: 'ITU MARS Ship Station List',
    url: 'https://www.itu.int/mmsapp/shipstation/list',
    description: 'База данных ITU MARS',
    icon: '🌐',
    color: 'bg-indigo-600 hover:bg-indigo-700',
  },
  INMARSAT: {
    name: 'Inmarsat Ships Directory',
    url: 'https://www.inmarsat.com/customer-service/ships-directory/',
    description: 'Официальный справочник судов Inmarsat',
    icon: '🛰️',
    color: 'bg-purple-600 hover:bg-purple-700',
  },
};

// ═══════════════════════════════════════════════════════════════
// ХЕЛПЕРЫ: склонение и локализация причин
// ═══════════════════════════════════════════════════════════════

function pluralizeSignals(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'сигналов';
  if (n1 > 1 && n1 < 5) return 'сигнала';
  if (n1 === 1) return 'сигнал';
  return 'сигналов';
}

const REASON_LABELS: Record<string, string> = {
  MMSI: 'MMSI совпадает',
  NAME_STRONG: 'Название судна совпадает',
  NAME_FUZZY: 'Название судна похоже',
  TIME: 'Время в пределах окна',
  IMN: 'Терминал совпадает',
  IMO: 'IMO совпадает',
};

function formatReason(reason: string): string {
  return REASON_LABELS[reason] ?? reason;
}

// ═══════════════════════════════════════════════════════════════
// АВТОРИЗАЦИЯ: Получение заголовков с токеном
// v3.2.0: Добавлено для всех API запросов
// ═══════════════════════════════════════════════════════════════

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ═══════════════════════════════════════════════════════════════
// КОМПОНЕНТ
// ═══════════════════════════════════════════════════════════════

export const UnmatchedSignalsPanel: React.FC<Props> = ({
  onSignalLinked,
  onSignalDismissed,
  onSignalsLoaded,
}) => {
  const [signals, setSignals] = useState<UnmatchedSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [dismissModal, setDismissModal] = useState<UnmatchedSignal | null>(null);
  const [lookupModal, setLookupModal] = useState<{
    signal: UnmatchedSignal;
    enrichment: SignalEnrichment;
  } | null>(null);
  const [manualRequestIds, setManualRequestIds] = useState<Record<number, string>>({});

  const shift = useMemo(() => getShiftPeriod(), []);
  const currentShiftSignals = useMemo(
    () =>
      signals.filter((s) => {
        const d = new Date(s.received_at);
        return d >= shift.start && d < shift.end;
      }),
    [signals, shift]
  );
  const previousSignals = useMemo(
    () => signals.filter((s) => new Date(s.received_at) < shift.start),
    [signals, shift]
  );

  // ═══════════════════════════════════════════════════════════════
  // v3.3.1 GROK FIX: useRef для стабилизации callback
  // Предотвращает пересоздание loadSignals при изменении onSignalsLoaded
  // ═══════════════════════════════════════════════════════════════
  const onSignalsLoadedRef = useRef(onSignalsLoaded);

  // Обновляем ref при изменении prop, но не триггерим useEffect
  useEffect(() => {
    onSignalsLoadedRef.current = onSignalsLoaded;
  }, [onSignalsLoaded]);

  const loadSignals = useCallback(async () => {
    const controller = new AbortController(); // v3.3.3: Cleanup pending requests
    try {
      setLoading(true);
      // v3.2.0: ИСПРАВЛЕНО - backticks + Authorization header
      const res = await fetch(apiUrl(`signals/unmatched?limit=50&sort=received_at&dir=desc`), {
        headers: getAuthHeaders(),
        signal: controller.signal, // v3.3.3: AbortController
      });
      if (!res.ok) throw new Error('Ошибка загрузки');
      const data = await res.json();
      const allSignals = Array.isArray(data) ? data : data.items || [];
      // ═══════════════════════════════════════════════════════════════
      // БИЗНЕС-ПРАВИЛО: Реальные тревоги (SSAS_ALERT) НЕ показываем
      // в панели несопоставленных сигналов - их НЕЛЬЗЯ связывать с заявками!
      // ═══════════════════════════════════════════════════════════════
      const testSignals = allSignals
        .filter((s: UnmatchedSignal) => !isRealAlert(s.signal_type))
        .map((s: UnmatchedSignal) => ({
          ...s,
          suggestions:
            typeof s.suggestions === 'string' ? JSON.parse(s.suggestions) : s.suggestions,
        }));
      setSignals(testSignals);

      // v3.3.1: Используем ref вместо прямого вызова prop
      onSignalsLoadedRef.current?.(testSignals);

      setError(null);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        // v3.3.3: Ignore abort errors
        setError(err instanceof Error ? err.message : 'Ошибка');
      }
    } finally {
      setLoading(false);
    }
  }, []); // ✅ ПУСТОЙ МАССИВ - loadSignals СТАБИЛЕН!
  // v3.3.3: Note: controller cleanup happens in try/catch, no return needed

  useEffect(() => {
    loadSignals();
    const interval = setInterval(loadSignals, 60000); // v3.3.3: 1 минута для production

    // v3.3.0: таблица может инициировать refresh без повторного API-фетча в другом компоненте
    const onRefresh = () => loadSignals();
    window.addEventListener('refresh-unmatched-signals', onRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refresh-unmatched-signals', onRefresh);
    };
  }, [loadSignals]);

  // Связать с заявкой
  const handleLink = async (signalId: number, requestId: number, override = false) => {
    try {
      setProcessing(true);
      // v3.2.0: ИСПРАВЛЕНО - backticks для template literal + Authorization header
      const res = await fetch(apiUrl(`signals/${signalId}/link`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ requestId, override }),
      });

      // Обработка конфликта терминалов (409)
      if (res.status === 409) {
        const errorData = await res.json().catch(() => ({}));
        const confirmed = window.confirm(
          `⚠️ Номера терминалов не совпадают!\n\n` +
            `Сигнал: ${errorData.signalTerminal || 'неизвестно'}\n` +
            `Заявка: ${errorData.requestSSAS || 'неизвестно'}\n\n` +
            `Связать принудительно?`
        );
        if (confirmed) {
          return handleLink(signalId, requestId, true);
        }
        return;
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Ошибка связывания');
      }

      onSignalLinked?.(signalId, requestId);
      await loadSignals();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Ошибка связывания');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualRequestIdChange = (signalId: number, value: string) => {
    setManualRequestIds((prev) => ({
      ...prev,
      [signalId]: value.replace(/[^\d]/g, ''),
    }));
  };

  const handleManualLinkById = (signalId: number) => {
    const rawRequestId = (manualRequestIds[signalId] || '').trim();
    if (!/^\d+$/.test(rawRequestId)) {
      alert('Укажите числовой ID заявки');
      return;
    }

    handleLink(signalId, Number(rawRequestId), false);
  };

  // Отклонить сигнал
  const handleDismiss = async (signalId: number, reason: string) => {
    try {
      setProcessing(true);
      const res = await fetch(apiUrl(`signals/${signalId}/dismiss`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error('Ошибка отклонения');
      onSignalDismissed?.(signalId, reason);
      await loadSignals();
      setDismissModal(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setProcessing(false);
    }
  };

  // Обогащение через vessel-cache (ITU MARS / кэш)
  const handleLookup = async (signal: UnmatchedSignal) => {
    try {
      setProcessing(true);
      const res = await fetch(
        apiUrl(`vessel-cache/lookup/${encodeURIComponent(signal.terminal_number)}`),
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );
      if (!res.ok) throw new Error('Ошибка обогащения');
      const json = await res.json();
      // Ответ: { success, data: { source, confidence, vessel_name, ... }, requiresManualCheck }
      const enrichment: SignalEnrichment = json.data || json;

      // Обновить данные сигнала в списке после обогащения
      if (enrichment.source !== 'NOT_FOUND') {
        setSignals((prev) =>
          prev.map((s) =>
            s.id === signal.id
              ? {
                  ...s,
                  vessel_name: enrichment.vessel_name || s.vessel_name,
                  mmsi: enrichment.mmsi || s.mmsi,
                  enrichment,
                }
              : s
          )
        );
      }

      setLookupModal({ signal, enrichment });

      // H3: Ask user before auto-opening external URL
      if (enrichment.auto_open_url) {
        const shouldOpen = window.confirm('Открыть страницу ITU MARS для ручной проверки?');
        if (shouldOpen) {
          window.open(enrichment.auto_open_url, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Ошибка обогащения');
    } finally {
      setProcessing(false);
    }
  };

  // PROTECTED: Открытие внешнего ресурса (ITU MARS / Inmarsat)
  const openExternalResource = (resourceKey: keyof typeof EXTERNAL_RESOURCES) => {
    const resource = EXTERNAL_RESOURCES[resourceKey];
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  // ═══════════════════════════════════════════════════════════════
  // РЕНДЕР
  // ═══════════════════════════════════════════════════════════════

  if (loading && signals.length === 0) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}
      >
        <div style={{ color: '#6b7280' }}>Загрузка сигналов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#fef2f2', padding: '16px', borderRadius: '8px' }}>
        <p style={{ color: '#991b1b' }}>⚠️ {error}</p>
        <button
          onClick={loadSignals}
          style={{
            marginTop: '8px',
            fontSize: '0.875rem',
            color: '#dc2626',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
          }}
        >
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Несопоставленные сигналы ({signals.length} {pluralizeSignals(signals.length)})
        </Typography>
        <Button variant="text" size="small" onClick={loadSignals} disabled={loading}>
          {loading ? '⟳ Обновление...' : '🔄 Обновить'}
        </Button>
      </div>

      {/* Список сигналов */}
      {signals.length === 0 ? (
        <div
          style={{
            backgroundColor: '#f0fdf4',
            padding: '32px',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: '#166534', fontWeight: 500, margin: 0 }}>
            ✅ Нет несопоставленных сигналов
          </p>
          <p style={{ color: '#16a34a', fontSize: '0.875rem', marginTop: '4px' }}>
            Все сигналы связаны с заявками
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
            ...currentShiftSignals,
            ...(previousSignals.length > 0 ? ['__SEPARATOR__' as any] : []),
            ...previousSignals,
          ].map((signal) =>
            signal === '__SEPARATOR__' ? (
              <div
                key="shift-separator"
                style={{ borderTop: '1px solid #e0e0e0', margin: '4px 0', paddingTop: '8px' }}
              >
                <span style={{ color: '#757575', fontSize: '13px' }}>
                  Из прошлых периодов ({previousSignals.length})
                </span>
              </div>
            ) : (
              <div
                key={signal.id}
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                }}
              >
                {/* Информация о сигнале */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <div>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      style={{ color: '#111827', margin: 0 }}
                    >
                      {signal.vessel_name || 'Неизвестное судно'}
                      {Array.isArray(signal.suggestions) &&
                        signal.suggestions.some((s: Suggestion) => s.score >= 80) && (
                          <span
                            style={{
                              display: 'inline-block',
                              backgroundColor: '#4caf50',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              marginLeft: '8px',
                            }}
                          >
                            Есть заявка
                          </span>
                        )}
                    </Typography>
                    <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: 0 }}>
                      Терминал:{' '}
                      <span style={{ fontFamily: 'monospace' }}>{signal.terminal_number}</span>
                    </p>
                    {signal.mmsi && (
                      <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: 0 }}>
                        MMSI: <span style={{ fontFamily: 'monospace' }}>{signal.mmsi}</span>
                      </p>
                    )}
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                      {new Date(signal.received_at).toLocaleString('ru-RU', { timeZone: 'UTC' }) +
                        ' UTC'}
                    </p>
                  </div>
                  {/* Badge signal_type убран по замечанию #5.5 */}
                </div>

                {/* Suggestions */}
                {signal.suggestions &&
                  Array.isArray(signal.suggestions) &&
                  signal.suggestions.length > 0 && (
                    <div
                      style={{
                        marginBottom: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#374151',
                          margin: 0,
                        }}
                      >
                        Возможные совпадения:
                      </p>
                      {signal.suggestions.slice(0, 3).map((sug: Suggestion) => (
                        <div
                          key={sug.requestId}
                          style={{
                            backgroundColor: '#eff6ff',
                            padding: '8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#1e3a5f',
                                margin: 0,
                              }}
                            >
                              Заявка #{sug.requestId}
                            </p>
                            <p style={{ fontSize: '0.75rem', color: '#1d4ed8', margin: 0 }}>
                              {sug.reasons.map(formatReason).join(', ')}
                            </p>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginLeft: '12px',
                            }}
                          >
                            <span
                              style={{ fontSize: '0.75rem', fontWeight: 500, color: '#1e40af' }}
                            >
                              {Math.round(sug.score * 100)}%
                            </span>
                            <button
                              onClick={() => handleLink(signal.id, sug.requestId)}
                              disabled={processing}
                              style={{
                                padding: '4px 12px',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                color: '#fff',
                                backgroundColor: '#2563eb',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                              }}
                            >
                              Связать
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                <div
                  style={{
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <input
                      value={manualRequestIds[signal.id] || ''}
                      onChange={(event) =>
                        handleManualRequestIdChange(signal.id, event.target.value)
                      }
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="ID заявки"
                      aria-label={`ID заявки для сигнала ${signal.id}`}
                      style={{
                        width: '120px',
                        padding: '6px 8px',
                        fontSize: '0.8125rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <button
                      onClick={() => handleManualLinkById(signal.id)}
                      disabled={processing || !/^\d+$/.test(manualRequestIds[signal.id] || '')}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: '#fff',
                        backgroundColor: '#047857',
                        border: 'none',
                        borderRadius: '4px',
                        cursor:
                          processing || !/^\d+$/.test(manualRequestIds[signal.id] || '')
                            ? 'not-allowed'
                            : 'pointer',
                        opacity:
                          processing || !/^\d+$/.test(manualRequestIds[signal.id] || '') ? 0.55 : 1,
                      }}
                    >
                      Связать по ID
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <button
                      onClick={() => handleLookup(signal)}
                      disabled={processing || !signal.terminal_number}
                      title="Поиск в локальной базе vessel-cache"
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: '#fff',
                        backgroundColor: '#2563eb',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processing || !signal.terminal_number ? 'not-allowed' : 'pointer',
                        opacity: processing || !signal.terminal_number ? 0.55 : 1,
                      }}
                    >
                      🔍 Поиск в кэше
                    </button>
                    <button
                      onClick={() => openExternalResource('ITU_MARS')}
                      title={EXTERNAL_RESOURCES.ITU_MARS.description}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: '#fff',
                        backgroundColor: '#4f46e5',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      {EXTERNAL_RESOURCES.ITU_MARS.icon} ITU MARS
                    </button>
                    <button
                      onClick={() => openExternalResource('INMARSAT')}
                      title={EXTERNAL_RESOURCES.INMARSAT.description}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: '#fff',
                        backgroundColor: '#7c3aed',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      {EXTERNAL_RESOURCES.INMARSAT.icon} Inmarsat
                    </button>
                    <button
                      onClick={() => setDismissModal(signal)}
                      disabled={processing}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: '#fff',
                        backgroundColor: '#6b7280',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.55 : 1,
                      }}
                    >
                      Отклонить
                    </button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Модальное окно отклонения */}
      {dismissModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '28rem',
              width: '100%',
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
              Отклонить сигнал
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '16px' }}>
              Укажите причину отклонения сигнала от{' '}
              {dismissModal.vessel_name || 'неизвестного судна'}
            </p>
            <textarea
              id="dismiss-reason"
              style={{
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px',
                fontSize: '0.875rem',
                boxSizing: 'border-box',
              }}
              rows={3}
              placeholder="Причина отклонения..."
            />
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}
            >
              <button
                onClick={() => setDismissModal(null)}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  color: '#374151',
                  backgroundColor: '#e5e7eb',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  const reason =
                    (document.getElementById('dismiss-reason') as HTMLTextAreaElement)?.value ||
                    'Нет причины';
                  handleDismiss(dismissModal.id, reason);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  color: '#fff',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Отклонить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно результата обогащения */}
      {lookupModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '42rem',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
              Результат обогащения
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* H1: Warning banner for incomplete data */}
              {lookupModal.enrichment.requiresManualCheck && (
                <div
                  style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: '6px',
                    padding: '12px',
                  }}
                >
                  <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', margin: 0 }}>
                    Данные неполные — требуется ручная проверка
                  </p>
                </div>
              )}
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                  Источник:
                </p>
                <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }}>
                  {lookupModal.enrichment.source}
                  {lookupModal.enrichment.origin_source &&
                    lookupModal.enrichment.source === 'CACHE' &&
                    ` (${lookupModal.enrichment.origin_source})`}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                  Достоверность:
                </p>
                <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }}>
                  {lookupModal.enrichment.confidence}%
                </p>
              </div>
              {lookupModal.enrichment.vessel_name && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    Название судна:
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }}>
                    {lookupModal.enrichment.vessel_name}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.imo_number && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    IMO:
                  </p>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#111827',
                      fontFamily: 'monospace',
                      margin: 0,
                    }}
                  >
                    {lookupModal.enrichment.imo_number}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.mmsi && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    MMSI:
                  </p>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#111827',
                      fontFamily: 'monospace',
                      margin: 0,
                    }}
                  >
                    {lookupModal.enrichment.mmsi}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.call_sign && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    Позывной:
                  </p>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#111827',
                      fontFamily: 'monospace',
                      margin: 0,
                    }}
                  >
                    {lookupModal.enrichment.call_sign}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.flag && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    Флаг:
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }}>
                    {lookupModal.enrichment.flag}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.owner && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    Владелец:
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#111827', margin: 0 }}>
                    {lookupModal.enrichment.owner}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.itu_mars_message && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', margin: 0 }}>
                    Сообщение:
                  </p>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      margin: 0,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {lookupModal.enrichment.itu_mars_message}
                  </p>
                </div>
              )}
              {lookupModal.enrichment.manual_check_urls &&
                lookupModal.enrichment.manual_check_urls.length > 0 && (
                  <div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '8px',
                      }}
                    >
                      Ссылки для проверки:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {lookupModal.enrichment.manual_check_urls.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            fontSize: '0.875rem',
                            color: '#2563eb',
                            textDecoration: 'underline',
                          }}
                        >
                          {link.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setLookupModal(null)}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  color: '#374151',
                  backgroundColor: '#e5e7eb',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnmatchedSignalsPanel;
